import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';

Chart.register(...registerables);

interface Props {
  location: string;
}

interface Row {
  location_name: string;
  geography_type: string;
  metric: string;
  value: number | string;
  year: number;
}

export const NeighborhoodOpportunityChart: React.FC<Props> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const distCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const distChartRef = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [hasData, setHasData] = useState<boolean | null>(null);
  const [distRows, setDistRows] = useState<Row[]>([]);
  const [activeChart, setActiveChart] = useState<'trend' | 'distribution'>('trend');
  const [activeView, setActiveView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');

  useEffect(() => {
    const load = async () => {
      if (!location) {
        setLoading(false);
        setHasData(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: locData, error: locErr } = await supabaseClient
          .from('neighborhood_opportunity')
          .select('location_name, geography_type, metric, value, year')
          .ilike('location_name', location)
          .order('year', { ascending: false })
          .limit(20);

        if (locErr) throw locErr;

        const { data: caData, error: caErr } = await supabaseClient
          .from('neighborhood_opportunity')
          .select('location_name, geography_type, metric, value, year')
          .eq('geography_type', 'state')
          .ilike('location_name', 'California')
          .order('year', { ascending: false })
          .limit(20);

        if (caErr) throw caErr;

        const allRows = ([...(locData || []), ...(caData || [])] as Row[]).filter(
          r => r.metric && r.value != null && r.year != null
        );

        if (!allRows.length) {
          setHasData(false);
          setLoading(false);
          return;
        }

        setRows(allRows);
        setHasData(true);
        setLoading(false);
      } catch (e: any) {
        console.error('NeighborhoodOpportunityChart: error loading data', e);
        setError(e?.message || 'Failed to load neighborhood opportunity data.');
        setHasData(false);
        setLoading(false);
      }
    };

    load();
  }, [location]);

  const summary = useMemo(() => {
    if (!rows.length) {
      return {
        years: [] as number[],
        locScores: {} as Record<number, number | null>,
        caScores: {} as Record<number, number | null>,
        latestYear: null as number | null,
        earliestYear: null as number | null,
        locScore: null as number | null,
        locCategory: null as string | null,
        caScore: null as number | null,
        caCategory: null as string | null
      };
    }

    const years = Array.from(new Set(rows.map(r => r.year))).sort((a, b) => a - b);

    const locScores: Record<number, number | null> = {};
    const caScores: Record<number, number | null> = {};

    years.forEach(year => {
      const yearRows = rows.filter(r => r.year === year);

      const findMetric = (geography_type: string | null, metric: string, isNumeric: boolean) => {
        const match = yearRows.find(
          r =>
            (geography_type
              ? r.geography_type === geography_type
              : r.location_name.toLowerCase() === location.toLowerCase()) && r.metric === metric
        );
        if (!match) return null;
        if (isNumeric) {
          const v = Number(match.value);
          return Number.isFinite(v) ? v : null;
        }
        return String(match.value);
      };

      locScores[year] = findMetric(null, 'score', true) as number | null;
      caScores[year] = findMetric('state', 'score', true) as number | null;
    });

    const earliestYear = years[0];
    const latestYear = years[years.length - 1];
    const latestYearRows = rows.filter(r => r.year === latestYear);

    const findLatestMetric = (geography_type: string | null, metric: string, isNumeric: boolean) => {
      const match = latestYearRows.find(
        r =>
          (geography_type
            ? r.geography_type === geography_type
            : r.location_name.toLowerCase() === location.toLowerCase()) && r.metric === metric
      );
      if (!match) return null;
      if (isNumeric) {
        const v = Number(match.value);
        return Number.isFinite(v) ? v : null;
      }
      return String(match.value);
    };

    const locScore = findLatestMetric(null, 'score', true) as number | null;
    const locCategory = (findLatestMetric(null, 'category', false) as string | null) ?? null;
    const caScore = findLatestMetric('state', 'score', true) as number | null;
    const caCategory = (findLatestMetric('state', 'category', false) as string | null) ?? null;

    return { years, locScores, caScores, latestYear, earliestYear, locScore, locCategory, caScore, caCategory };
  }, [rows, location]);

  useEffect(() => {
    if (activeChart !== 'trend') {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {}
        chartRef.current = null;
      }
      return;
    }
    if (!canvasRef.current || !hasData || !summary.latestYear || !summary.years.length) return;

    const { years, locScores, caScores } = summary;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch {}
      chartRef.current = null;
    }

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: years,
        datasets: [
          {
            type: 'bar' as const,
            label: `${location} score`,
            data: years.map(y => (locScores[y] != null ? locScores[y] : null)),
            backgroundColor: '#3b82f6',
            borderColor: '#1d4ed8',
            borderWidth: 1
          },
          {
            type: 'line' as const,
            label: 'California average',
            data: years.map(y => (caScores[y] != null ? caScores[y] : null)),
            borderColor: '#10b981',
            backgroundColor: 'transparent',
            tension: 0.25,
            borderWidth: 2,
            pointRadius: 3,
            yAxisID: 'y'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: standardLegend,
          title: standardTitle('Neighborhood Opportunity Score Over Time')
        },
        scales: {
          x: {
            title: { display: true, text: 'Year' }
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Opportunity Score' }
          }
        }
      }
    });

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {}
        chartRef.current = null;
      }
    };
  }, [activeChart, hasData, summary, location]);

  useEffect(() => {
    if (!summary.latestYear) return;
    const loadDistribution = async () => {
      try {
        const { data, error: distErr } = await supabaseClient
          .from('neighborhood_opportunity')
          .select('location_name, geography_type, metric, value, year')
          .eq('metric', 'score')
          .eq('geography_type', 'place')
          .eq('year', summary.latestYear);

        if (distErr) {
          console.warn('NeighborhoodOpportunityChart: error loading distribution data', distErr);
          return;
        }

        setDistRows((data || []) as Row[]);
      } catch (e) {
        console.warn('NeighborhoodOpportunityChart: distribution load failed', e);
      }
    };

    loadDistribution();
  }, [summary.latestYear]);

  useEffect(() => {
    if (activeChart !== 'distribution') {
      if (distChartRef.current) {
        try {
          distChartRef.current.destroy();
        } catch {}
        distChartRef.current = null;
      }
      return;
    }
    if (!distCanvasRef.current || !distRows.length || !summary.latestYear) return;

    const ctx = distCanvasRef.current.getContext('2d');
    if (!ctx) return;

    if (distChartRef.current) {
      try {
        distChartRef.current.destroy();
      } catch {}
      distChartRef.current = null;
    }

    const points = distRows.map((r, idx) => ({
      x: Number(r.value),
      y: (Math.random() - 0.5) * 0.4, // small vertical jitter
      label: r.location_name
    }));

    const locPoint = points.find(
      p => p.label.toLowerCase() === location.toLowerCase()
    );

    distChartRef.current = new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: 'All places',
            data: points,
            backgroundColor: 'rgba(148, 163, 184, 0.7)',
            pointRadius: 3
          },
          ...(locPoint
            ? [
                {
                  label: location,
                  data: [locPoint],
                  backgroundColor: '#3b82f6',
                  pointRadius: 6
                }
              ]
            : [])
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(
            `Distribution of Opportunity Scores Across Places (${summary.latestYear})`
          ),
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const label = ctx.raw?.label || '';
                const v = ctx.parsed?.x;
                if (v == null) return label;
                return `${label}: ${v.toFixed(1)}`;
              }
            }
          }
        },
        scales: {
          x: {
            title: { display: true, text: 'Opportunity Score' }
          },
          y: {
            display: false
          }
        }
      }
    });

    return () => {
      if (distChartRef.current) {
        try {
          distChartRef.current.destroy();
        } catch {}
        distChartRef.current = null;
      }
    };
  }, [activeChart, distRows, summary.latestYear, location]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Neighborhood_Opportunity_Score');
    downloadChartAsPNG(canvasRef, filename);
  };

  const handleDownloadCSV = () => {
    downloadDataAsCSV(rows, formatFilename(location, 'Neighborhood_Opportunity_Data'));
  };

  if (loading) {
    return (
      <div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading neighborhood opportunity data for {location}…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 360, padding: 24, textAlign: 'center' }}>
        <div style={{ color: '#dc2626', marginBottom: 8 }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: 14 }}>Location: {location || 'Not set'}</div>
      </div>
    );
  }

  if (!hasData || !summary.latestYear) {
    return (
      <div style={{ height: 360, padding: 24, textAlign: 'center' }}>
        <div>No neighborhood opportunity data available for {location}.</div>
      </div>
    );
  }

  const { latestYear, earliestYear, locScore, locCategory, caScore, caCategory } = summary;

  const keyMetricsDisplay =
    latestYear != null && locScore != null ? (
      <KeyMetrics>
        <KeyMetric
          value={locScore.toFixed(1)}
          label={`Opportunity score (${latestYear})`}
          color="#3b82f6"
        />
        {caScore != null && (
          <KeyMetric
            value={caScore.toFixed(1)}
            label={`California average (${latestYear})`}
            color="#10b981"
          />
        )}
        {locCategory && (
          <KeyMetric
            value={locCategory.charAt(0).toUpperCase() + locCategory.slice(1)}
            label="Local category"
            color="#6366f1"
          />
        )}
      </KeyMetrics>
    ) : null;

  const overviewContent = (
    <OverviewContent
      title="Overview"
      description={`The neighborhood opportunity score summarizes access to high-opportunity neighborhoods in ${location}, based on factors like income, education, and environment.`}
    >
      <p>
        Higher scores indicate residents have greater access to neighborhoods with strong economic, educational, and
        environmental conditions. Comparing your community’s score to the California average highlights whether local
        opportunity is relatively high or low.
      </p>
    </OverviewContent>
  );

  const relevanceContent = (
    <RelevanceContent
      title="Relevance"
      description="Neighborhood opportunity is closely tied to long-term economic mobility, health, and climate resilience."
    >
      <div className="methodology-details-simple">
        <h5>Economic Mobility</h5>
        <p>
          Children who grow up in high-opportunity neighborhoods are more likely to achieve higher incomes as adults,
          making neighborhood quality a key driver of long-term economic outcomes.
        </p>
      </div>
      <div className="methodology-details-simple">
        <h5>Climate & Infrastructure Investment</h5>
        <p>
          Understanding which communities have lower neighborhood opportunity can guide where to prioritize
          investments in housing, transit, parks, and resilience infrastructure.
        </p>
      </div>
    </RelevanceContent>
  );

  const equityContent = (
    <EquityContent
      title="Equity"
      description="Neighborhood opportunity shows how access to safe, resource-rich neighborhoods is distributed across communities."
    >
      <div className="methodology-details-simple">
        <h5>Segregation of Opportunity</h5>
        <p>
          Large gaps between a community’s opportunity score and the state average may reflect segregation by income or
          race, and can compound other inequities in housing, education, and health.
        </p>
      </div>
      <div className="methodology-details-simple">
        <h5>Targeting Support</h5>
        <p>
          Pairing neighborhood opportunity data with local knowledge helps identify areas where residents face barriers
          to opportunity and where supportive policies can have the greatest impact.
        </p>
      </div>
    </EquityContent>
  );

  const methodologyContent = (
    <MethodologyContent
      title="Data Sources and Methodology"
      description="Neighborhood opportunity scores are based on the California State Treasurer’s opportunity maps and related indicators."
      dataSources={[
        "California State Treasurer's Office – Neighborhood Opportunity Maps and underlying indicators",
        'Local geographic crosswalks used to align scores with city and county boundaries'
      ]}
      methodology={[
        'Scores are derived from multiple indicators related to income, education, housing, environment, and other factors.',
        'For each geography, the latest available opportunity score is used for comparison.',
        'Scores for California represent a statewide average, providing a benchmark for local values.'
      ]}
    />
  );

  const chartContent = activeChart === 'trend' ? (
    <div style={{ height: 320, width: '100%', position: 'relative' }}>
      <canvas ref={canvasRef} style={{ height: '100%', width: '100%' }} />
    </div>
  ) : (
    <div style={{ height: 320, width: '100%', position: 'relative' }}>
      <canvas ref={distCanvasRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  return (
    <ChartLayout
      title="Neighborhood Opportunity Score"
      key={`neighborhood-opportunity-${location}`}
      description={`${location} • Source: California Neighborhood Opportunity data`}
      chartType="bar"
      chartSelector={{
        value: activeChart,
        onChange: (value: string) => setActiveChart(value as 'trend' | 'distribution'),
        options: [
          { value: 'trend', label: 'Score Trend (Bar + Line)' },
          { value: 'distribution', label: 'Score Distribution' }
        ]
      }}
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      activeView={activeView}
      onViewChange={(view: string) => setActiveView(view as 'overview' | 'relevance' | 'equity' | 'methodology')}
      showRelevanceEquity={true}
      layout="side-by-side"
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={formatFilename(location, 'Neighborhood_Opportunity_Score')}
      csvFilename={formatFilename(location, 'Neighborhood_Opportunity_Data')}
      enableComments={true}
      location={location}
      overviewContent={overviewContent}
      relevanceContent={relevanceContent}
      equityContent={equityContent}
      methodologyContent={methodologyContent}
    />
  );
};

export default NeighborhoodOpportunityChart;

